import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api/admin'
  : `http://${window.location.hostname}:4001/api/admin`;

// The session is an httpOnly cookie set by the backend — JavaScript never sees
// the token. withCredentials sends the cookie (needed cross-port in dev), and
// X-ShopBook-Client is the anti-CSRF header the backend requires on writes.
// timeout: an unreachable backend surfaces as an error after 15s instead of
// leaving the UI spinning indefinitely.
const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json', 'X-ShopBook-Client': '1' },
});

// Tokens from before the cookie change are no longer used.
try { localStorage.removeItem('shopbook_admin_token'); } catch { /* storage blocked */ }

// Session expired or was signed out elsewhere: tell the app to show the login
// page. The 401s from login ("Invalid credentials") and change-password
// ("Current password is incorrect") are form errors, left to the form.
const FORM_401 = ['/auth/login', '/auth/change-password'];
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && !FORM_401.includes(err.config?.url)) {
      window.dispatchEvent(new Event('shopbook-admin-signed-out'));
    }
    return Promise.reject(err);
  }
);

export const adminAuthAPI = {
  login:          (data) => api.post('/auth/login', data),
  logout:         ()     => api.post('/auth/logout'),
  me:             ()     => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword:  (data) => api.post('/auth/reset-password', data),
};

export const adminDashboardAPI = {
  overview:  () => api.get('/dashboard'),
  companies: () => api.get('/dashboard/companies'),
  users:     () => api.get('/dashboard/users'),
  config:    () => api.get('/dashboard/config'),
};

export const adminBusinessAPI = {
  list:       () => api.get('/businesses'),
  getById:    (id) => api.get(`/businesses/${id}`),
  deactivate: (id) => api.post(`/businesses/${id}/deactivate`),
  activate:   (id) => api.post(`/businesses/${id}/activate`),
};

export const adminCompanyAPI = {
  deactivate:  (id)       => api.post(`/companies/${id}/deactivate`),
  activate:    (id)       => api.post(`/companies/${id}/activate`),
  updateInfo:  (id, data) => api.patch(`/companies/${id}`, data),
  setPlan:     (id, data) => api.patch(`/companies/${id}/plan`, data),
  setComplimentary: (id, data) => api.patch(`/companies/${id}/complimentary`, data),
  planHistory: (id)       => api.get(`/companies/${id}/plan-history`),
  remove:      (id, confirm_name) => api.delete(`/companies/${id}`, { data: { confirm_name } }),
};

export const adminUpgradeAPI = {
  list:    (status = 'pending') => api.get(`/upgrade-requests?status=${status}`),
  approve: (id, data)           => api.post(`/upgrade-requests/${id}/approve`, data),
  reject:  (id, data)           => api.post(`/upgrade-requests/${id}/reject`, data),
};

export const adminUserAPI = {
  list:       () => api.get('/users'),
  getById:    (id) => api.get(`/users/${id}`),
  deactivate: (id) => api.post(`/users/${id}/deactivate`),
  reactivate: (id) => api.post(`/users/${id}/reactivate`),
};

export const adminAnalyticsAPI = {
  overview: ()             => api.get('/analytics/overview'),
  users:    (window = 30)  => api.get('/analytics/users', { params: { window } }),
};

export default api;
